/**
 * AdminPdfConservationPage — Phase 2C.C-ext T2 (2026-09-08, PDF v7)
 *
 * Page admin `/admin/pdf-conservation` : audit read-only du tenant courant
 * pour lister les documents dont le PDF n'a jamais été stocké ou dont la
 * génération a échoué. Aucun bouton "Régénérer" à cette étape — vue seulement.
 *
 * Endpoint : GET /api/admin/pdf-conservation-audit
 * Rôle : admin only (commercial → 403 via ProtectedRoute).
 */
import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, RefreshCw, AlertTriangle, FileWarning, ShieldAlert, ShieldCheck } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Collapsible, CollapsibleContent, CollapsibleTrigger,
} from "@/components/ui/collapsible";

// Statuts contractuels où count > 0 = alerte
const CONTRACTUAL_QUOTE_STATUSES = new Set(["envoye", "signe"]);
const CONTRACTUAL_INVOICE_STATUSES = new Set(["emitted", "paid", "pending", "overdue"]);

function StatusRow({ entity, status, bucket, contractualSet }) {
  const isContractual = contractualSet.has(status);
  const alert = isContractual && bucket.count > 0;
  const [open, setOpen] = useState(false);
  const numbers = bucket.numbers || [];
  return (
    <div
      className={`rounded-md border p-3 ${
        alert ? "border-red-800/60 bg-red-950/30" : "border-slate-800 bg-slate-950/40"
      }`}
      data-testid={`conservation-row-${entity}-${status}`}
    >
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2 text-sm">
          <span className="font-mono text-slate-300">{status}</span>
          {alert && (
            <Badge variant="destructive" className="gap-1" data-testid={`conservation-alert-${entity}-${status}`}>
              <AlertTriangle className="h-3 w-3" />
              Contractuel
            </Badge>
          )}
          {isContractual && !alert && (
            <Badge variant="secondary" className="text-xs">
              Contractuel
            </Badge>
          )}
        </div>
        <div className="flex items-center gap-2">
          <span
            className={`font-mono text-lg tabular-nums ${
              alert ? "text-red-300" : "text-slate-200"
            }`}
            data-testid={`conservation-count-${entity}-${status}`}
          >
            {bucket.count}
          </span>
          <span className="text-xs text-slate-500">sans PDF</span>
        </div>
      </div>
      {numbers.length > 0 && (
        <Collapsible open={open} onOpenChange={setOpen} className="mt-2">
          <CollapsibleTrigger asChild>
            <button
              type="button"
              className="text-xs text-slate-400 underline hover:text-slate-200"
              data-testid={`conservation-toggle-${entity}-${status}`}
            >
              {open ? "Masquer" : "Voir"} les numéros
              {bucket.truncated && " (100 premiers)"}
            </button>
          </CollapsibleTrigger>
          <CollapsibleContent>
            <div className="mt-2 grid grid-cols-2 gap-x-4 gap-y-1 rounded border border-slate-800 bg-slate-950 p-3 sm:grid-cols-3 lg:grid-cols-4">
              {numbers.map((n) => (
                <span
                  key={n}
                  className="font-mono text-xs text-slate-300"
                  data-testid={`conservation-number-${n || "empty"}`}
                >
                  {n || "(sans numéro)"}
                </span>
              ))}
            </div>
            {bucket.truncated && (
              <p className="mt-1 text-xs text-slate-500">
                Liste tronquée à 100 numéros ; export complet non disponible dans cette vue.
              </p>
            )}
          </CollapsibleContent>
        </Collapsible>
      )}
    </div>
  );
}

function ErrorRow({ entity, doc }) {
  const to = entity === "quote" ? `/quotes/${doc.id}` : `/invoices/${doc.id}`;
  return (
    <div
      className="rounded-md border border-orange-800/60 bg-orange-950/30 p-3"
      data-testid={`conservation-error-${entity}-${doc.id}`}
    >
      <div className="flex items-center justify-between gap-2">
        <Link
          to={to}
          className="font-mono text-sm text-orange-200 underline hover:text-orange-100"
          data-testid={`conservation-error-link-${doc.id}`}
        >
          {doc.number || "(sans numéro)"}
        </Link>
        <Badge variant="outline" className="text-xs">
          {doc.status}
        </Badge>
      </div>
      <p
        className="mt-1 break-words font-mono text-xs text-orange-200/80"
        data-testid={`conservation-error-msg-${doc.id}`}
      >
        {doc.error}
      </p>
      {doc.updated_at && (
        <p className="mt-1 text-xs text-slate-500">
          Dernière tentative : {doc.updated_at.slice(0, 19).replace("T", " ")}
        </p>
      )}
    </div>
  );
}

export default function AdminPdfConservationPage() {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get("/admin/pdf-conservation-audit");
      setData(res.data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const renderSection = (entity, title, sectionData, contractualSet, statusOrder) => {
    const by = sectionData?.by_status_missing_blob || {};
    const errs = sectionData?.with_generation_error;
    const totalMissing = Object.values(by).reduce((sum, b) => sum + (b?.count || 0), 0);
    const contractualMissing = Object.entries(by)
      .filter(([s]) => contractualSet.has(s))
      .reduce((sum, [, b]) => sum + (b?.count || 0), 0);
    return (
      <Card className="border-slate-800 bg-slate-900/40" data-testid={`conservation-section-${entity}`}>
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <CardTitle className="text-base text-slate-100">{title}</CardTitle>
            <div className="flex items-center gap-3 text-sm">
              <span className="text-slate-400">
                Total sans PDF :{" "}
                <span className="font-mono tabular-nums text-slate-200">{totalMissing}</span>
              </span>
              {contractualMissing > 0 && (
                <Badge variant="destructive" className="gap-1">
                  <AlertTriangle className="h-3 w-3" />
                  {contractualMissing} contractuel{contractualMissing > 1 ? "s" : ""}
                </Badge>
              )}
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-3">
          {statusOrder.map((status) => (
            <StatusRow
              key={status}
              entity={entity}
              status={status}
              bucket={by[status] || { count: 0, numbers: [], truncated: false }}
              contractualSet={contractualSet}
            />
          ))}
          {errs && (
            <div className="mt-4 space-y-2">
              <div className="flex items-center gap-2 text-sm text-slate-300">
                <FileWarning className="h-4 w-4 text-orange-400" />
                Erreurs de génération PDF (tracées via <code>pdf_generation_error</code>) :{" "}
                <span className="font-mono tabular-nums text-orange-200">{errs.count}</span>
                {errs.truncated && (
                  <span className="text-xs text-slate-500">(100 premiers)</span>
                )}
              </div>
              {errs.count === 0 ? (
                <p className="text-xs text-slate-500" data-testid={`conservation-error-empty-${entity}`}>
                  Aucune erreur de génération PDF tracée dans ce tenant.
                </p>
              ) : (
                <div className="grid gap-2 sm:grid-cols-2">
                  {(errs.items || []).map((doc) => (
                    <ErrorRow key={doc.id} entity={entity} doc={doc} />
                  ))}
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>
    );
  };

  return (
    <div className="space-y-4 p-4 sm:p-6">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-xl font-semibold text-slate-100" data-testid="pdf-conservation-title">
            Audit conservation PDF
          </h1>
          <p className="mt-1 text-sm text-slate-400">
            Documents dont le PDF n&apos;a jamais été stocké ou dont la génération a échoué.
            Vue read-only du tenant courant.
          </p>
          {data?.current_template_version !== undefined && (
            <p className="mt-1 text-xs text-slate-500">
              Version template PDF courante : v{data.current_template_version} ·{" "}
              {data.generated_at && `Généré le ${data.generated_at.slice(0, 19).replace("T", " ")} UTC`}
            </p>
          )}
        </div>
        <div className="flex gap-2">
          <Button
            variant="outline"
            onClick={load}
            disabled={loading}
            data-testid="pdf-conservation-refresh-btn"
          >
            {loading ? (
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            ) : (
              <RefreshCw className="mr-2 h-4 w-4" />
            )}
            Actualiser
          </Button>
        </div>
      </div>

      <Card className="border-blue-900/40 bg-blue-950/20">
        <CardContent className="flex items-start gap-3 p-4">
          <ShieldAlert className="mt-0.5 h-4 w-4 flex-shrink-0 text-blue-300" />
          <p className="text-xs leading-relaxed text-blue-100/90">
            Cette page est <strong>read-only</strong>. Elle n&apos;actionne aucune régénération.
            Pour régénérer des blobs obsolètes, utilisez la page{" "}
            <Link to="/admin/pdf-cache" className="underline">
              /admin/pdf-cache
            </Link>
            . Les documents contractuels signés / factures émises sont figés — voir garde-fous
            backend (403 <code>skip_signed</code> / <code>skip_emitted_invoice</code>).
          </p>
        </CardContent>
      </Card>

      {loading && !data && (
        <div className="flex items-center gap-2 text-slate-400" data-testid="pdf-conservation-loading">
          <Loader2 className="h-4 w-4 animate-spin" /> Chargement...
        </div>
      )}

      {/* Fallback rétrocompat : si l'API ne renvoie PAS `sanity_check`, on
          affiche un bandeau jaune qui indique que le backend n'expose pas
          encore le recoupement indépendant (Republish backend nécessaire).
          Évite l'invisibilité totale du bloc en Prod entre 2 déploiements. */}
      {data && !data.sanity_check && (
        <div
          className="flex items-start gap-2 rounded-md border border-amber-800/50 bg-amber-950/30 px-3 py-2"
          data-testid="pdf-conservation-sanity-missing"
        >
          <ShieldAlert className="mt-0.5 h-4 w-4 flex-shrink-0 text-amber-400" />
          <p className="text-xs text-amber-200">
            Sanity check indépendant non disponible dans cette version du backend.
            Les chiffres ci-dessous ne sont pas recoupés — <strong>Republish
            backend requis</strong> pour activer le contrôle d&apos;intégrité.
          </p>
        </div>
      )}

      {data?.sanity_check && (() => {
        const sc = data.sanity_check;
        const allMatch = sc.quotes?.match === true && sc.invoices?.match === true;
        // Lot P0 UI 3-states (2026-09-09) — Calcul des 3 métriques
        // (blobs présents / champs vides / ghost) sur le périmètre AUDITÉ
        // pour chaque collection. Doit être affiché SYSTÉMATIQUEMENT (vert
        // OU rouge) pour prouver que le vérificateur d'intégrité a tourné.
        //
        //   X_present = computed_with_blob - blob_missing_ghost_count
        //     (docs avec pdf_file_id set ET blob GridFS présent)
        //   Y_empty   = sum_of_status_buckets - computed_with_blob
        //     (docs sans pdf_file_id → champs vides)
        //   Z_ghost   = blob_missing_ghost_count
        //     (docs avec pdf_file_id set MAIS blob GridFS absent)
        //
        // Somme X + Y + Z = sum_of_status_buckets (docs dans statuts audités).
        const computeStates = (bucket) => {
          const total = bucket?.total_non_deleted_in_collection || 0;
          const withBlob = bucket?.computed_with_blob || 0;
          const sumBuckets = bucket?.sum_of_status_buckets || 0;
          const ghost = bucket?.blob_missing_ghost_count || 0;
          return {
            total,
            present: Math.max(0, withBlob - ghost),
            empty: Math.max(0, sumBuckets - withBlob),
            ghost,
          };
        };
        const q = computeStates(sc.quotes);
        const i = computeStates(sc.invoices);
        if (allMatch) {
          return (
            <div
              className="flex items-start gap-2 rounded-md border border-green-800/50 bg-green-950/30 px-3 py-2"
              data-testid="pdf-conservation-sanity-ok"
            >
              <ShieldCheck className="mt-0.5 h-4 w-4 flex-shrink-0 text-green-400" />
              <p className="text-xs leading-relaxed text-green-200">
                Audit intègre —{" "}
                <span className="font-mono tabular-nums">{q.total}</span>{" "}
                devis (
                <span className="font-mono tabular-nums" data-testid="sanity-quotes-present-count">
                  {q.present}
                </span>{" "}
                blobs présents,{" "}
                <span className="font-mono tabular-nums" data-testid="sanity-quotes-empty-count">
                  {q.empty}
                </span>{" "}
                champs vides,{" "}
                <span className="font-mono tabular-nums" data-testid="sanity-quotes-ghost-count">
                  {q.ghost}
                </span>{" "}
                ghost),{" "}
                <span className="font-mono tabular-nums">{i.total}</span>{" "}
                factures (
                <span className="font-mono tabular-nums" data-testid="sanity-invoices-present-count">
                  {i.present}
                </span>{" "}
                blobs présents,{" "}
                <span className="font-mono tabular-nums" data-testid="sanity-invoices-empty-count">
                  {i.empty}
                </span>{" "}
                champs vides,{" "}
                <span className="font-mono tabular-nums" data-testid="sanity-invoices-ghost-count">
                  {i.ghost}
                </span>{" "}
                ghost) recoupés par sanity check indépendant.
              </p>
            </div>
          );
        }
        const detailParts = [];
        if (sc.quotes?.match === false) {
          detailParts.push(
            `devis : total=${sc.quotes.total_non_deleted_in_collection}, ` +
            `buckets=${sc.quotes.sum_of_status_buckets}, ` +
            `orphans=${sc.quotes.orphan_count}` +
            (sc.quotes.blob_missing_ghost_count > 0
              ? `, ghost_blobs=${sc.quotes.blob_missing_ghost_count}`
              : "")
          );
        }
        if (sc.invoices?.match === false) {
          detailParts.push(
            `factures : total=${sc.invoices.total_non_deleted_in_collection}, ` +
            `buckets=${sc.invoices.sum_of_status_buckets}, ` +
            `orphans=${sc.invoices.orphan_count}` +
            (sc.invoices.blob_missing_ghost_count > 0
              ? `, ghost_blobs=${sc.invoices.blob_missing_ghost_count}`
              : "")
          );
        }
        // Lot P0 Vérificateur intégrité (2026-09-09) — Signal spécifique
        // pour l'état `blob_missing_ghost` : documents avec pdf_file_id
        // set mais blob GridFS absent. Le lazy_regen sert un PDF
        // régénéré silencieusement, mais la traçabilité forensique est
        // compromise (impossible de reproduire le PDF original du signage).
        const ghostQ = sc.quotes?.blob_missing_ghost_count || 0;
        const ghostI = sc.invoices?.blob_missing_ghost_count || 0;
        const hasGhosts = ghostQ > 0 || ghostI > 0;
        return (
          <div
            className="rounded-md border-2 border-red-700 bg-red-950/50 p-4"
            data-testid="pdf-conservation-sanity-failed"
            role="alert"
          >
            <div className="flex items-start gap-3">
              <AlertTriangle className="mt-0.5 h-5 w-5 flex-shrink-0 text-red-300" />
              <div className="space-y-1 text-sm">
                <p className="font-semibold text-red-100">
                  {hasGhosts
                    ? `⚠️ Blobs contractuels perdus détectés (${ghostQ} devis, ${ghostI} factures)`
                    : "⚠️ Audit incomplet — recoupement indépendant en échec"}
                </p>
                {hasGhosts && (
                  <p
                    className="text-xs leading-relaxed text-red-100/90"
                    data-testid="pdf-conservation-ghost-alert"
                  >
                    Documents avec <code>pdf_file_id</code> set mais blob GridFS
                    absent → régénération silencieuse en cours via lazy_regen.
                    Traçabilité forensique compromise (impossible de reproduire
                    le PDF original du signage).
                  </p>
                )}
                <p className="text-xs leading-relaxed text-red-200/90">
                  {detailParts.join(" · ")}
                </p>
                {/* Lot P0 UI 3-states (2026-09-09) — Détail X/Y/Z rendu
                    aussi en cas d'échec pour cohérence UX + non-régression
                    du contrat "3 compteurs visibles quel que soit l'état". */}
                <p className="text-xs leading-relaxed text-red-100/90">
                  <span className="font-mono tabular-nums">{q.total}</span>{" "}
                  devis (
                  <span className="font-mono tabular-nums" data-testid="sanity-quotes-present-count">
                    {q.present}
                  </span>{" "}
                  blobs présents,{" "}
                  <span className="font-mono tabular-nums" data-testid="sanity-quotes-empty-count">
                    {q.empty}
                  </span>{" "}
                  champs vides,{" "}
                  <span className="font-mono tabular-nums" data-testid="sanity-quotes-ghost-count">
                    {q.ghost}
                  </span>{" "}
                  ghost),{" "}
                  <span className="font-mono tabular-nums">{i.total}</span>{" "}
                  factures (
                  <span className="font-mono tabular-nums" data-testid="sanity-invoices-present-count">
                    {i.present}
                  </span>{" "}
                  blobs présents,{" "}
                  <span className="font-mono tabular-nums" data-testid="sanity-invoices-empty-count">
                    {i.empty}
                  </span>{" "}
                  champs vides,{" "}
                  <span className="font-mono tabular-nums" data-testid="sanity-invoices-ghost-count">
                    {i.ghost}
                  </span>{" "}
                  ghost).
                </p>
                <p className="text-xs text-red-200/80">
                  Ne pas se fier aux chiffres ci-dessous tant que le recoupement n&apos;est
                  pas résolu. Consulter les logs serveur (
                  <code>pdf_conservation_audit sanity_check FAILED</code>) pour identifier
                  les documents orphelins ou le décalage. Cause probable :
                  documents avec statut hors périmètre audité (
                  <code>brouillon/envoye/signe/refuse/expire</code> pour devis,
                  <code>pending/paid/overdue/emitted</code> pour factures).
                </p>
              </div>
            </div>
          </div>
        );
      })()}

      {data && (
        <>
          {renderSection(
            "quote",
            "Devis",
            data.quotes,
            CONTRACTUAL_QUOTE_STATUSES,
            ["brouillon", "envoye", "signe", "refuse", "expire"],
          )}
          {renderSection(
            "invoice",
            "Factures",
            data.invoices,
            CONTRACTUAL_INVOICE_STATUSES,
            ["pending", "paid", "overdue", "emitted"],
          )}
        </>
      )}
    </div>
  );
}
